import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { AppDispatch, RootState } from '../../../Redux/Store';
import {
  createInquiry,
  deleteInquiry,
  fetchInquiries,
  updateInquiry,
} from '../../../Redux/ReservationSettings/reservationSettingsApi';
import { InquiryType, ReservationInquiry } from '../../../types/reservation';
import ConfirmDialog from '../../../Components/Dialog/ConfirmDialog';
import { SavedSnackbar, TabDescription, useSaveFeedback } from './SettingsShared';

interface Props {
  storeId: string;
}

/** 質問形式の表示ラベル（既存データに NUMBER/BOOLEAN があっても表示だけは対応する） */
const TYPE_LABELS: Record<InquiryType, string> = {
  TEXT: '自由記述',
  NUMBER: '数値',
  CHOICE: '選択式',
  BOOLEAN: 'はい/いいえ',
};

/** 追加/編集ダイアログのフォーム状態 */
interface InquiryForm {
  type: InquiryType;
  content: string;
  required: boolean;
  choices: string[];
}

const emptyForm: InquiryForm = { type: 'TEXT', content: '', required: false, choices: [] };

/**
 * 飲食予約でよく使う質問テンプレート。選ぶと追加フォームにプリフィルし、
 * 店舗が内容を確認・調整してから保存できる（そのまま保存はしない）。
 */
const INQUIRY_SAMPLES: { label: string; form: InquiryForm }[] = [
  {
    label: 'アレルギー・苦手な食材',
    form: {
      type: 'TEXT',
      content: 'アレルギーや苦手な食材があればご記入ください（例：えび、そば、乳製品など）',
      required: false,
      choices: [],
    },
  },
  {
    label: 'ご利用シーン',
    form: {
      type: 'CHOICE',
      content: '本日のご利用シーンを教えてください',
      required: false,
      choices: ['お食事', '接待・会食', 'デート', '記念日・お祝い', '女子会・お集まり', 'その他'],
    },
  },
  {
    label: '記念日・お祝いの有無',
    form: {
      type: 'TEXT',
      content: '記念日やお祝いのご予定があればお知らせください（例：誕生日、結婚記念日など）',
      required: false,
      choices: [],
    },
  },
  {
    label: 'お席のご希望',
    form: {
      type: 'CHOICE',
      content: 'お席のご希望があればお選びください（ご希望に添えない場合がございます）',
      required: false,
      choices: ['おまかせ', 'カウンター席', 'テーブル席', '個室'],
    },
  },
  {
    label: 'お子様の同伴',
    form: {
      type: 'CHOICE',
      content: 'お子様のご同伴はありますか？',
      required: false,
      choices: ['なし', 'あり'],
    },
  },
  {
    label: '当日の連絡先',
    form: {
      type: 'TEXT',
      content: '当日ご連絡がつきやすいお電話番号があればご記入ください',
      required: false,
      choices: [],
    },
  },
  {
    label: 'ご要望・ご質問',
    form: {
      type: 'TEXT',
      content: 'その他ご要望やご質問がございましたらご記入ください',
      required: false,
      choices: [],
    },
  },
];

/**
 * タブ⑥質問。公開予約フォームでお客様に尋ねる質問項目の CRUD。
 * CHOICE（選択式）の場合のみ選択肢を編集できる。
 */
const InquiriesTab: React.FC<Props> = ({ storeId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const inquiries = useSelector((state: RootState) => state.reservationSettings.inquiries);

  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ReservationInquiry | null>(null);
  const [form, setForm] = useState<InquiryForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReservationInquiry | null>(null);
  const [samplesOpen, setSamplesOpen] = useState(false);
  const { savedOpen, closeSaved, showSaved, error, setError, showError } = useSaveFeedback();

  useEffect(() => {
    dispatch(fetchInquiries(storeId))
      .unwrap()
      .catch(showError)
      .finally(() => setLoaded(true));
  }, [dispatch, storeId, showError]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError(null);
    setDialogOpen(true);
  };

  /** サンプルテンプレートを追加フォームにプリフィルして開く（そのまま保存せず店舗が確認・調整する） */
  const openSample = (sample: InquiryForm) => {
    setEditing(null);
    setForm({ ...sample, choices: [...sample.choices] });
    setFormError(null);
    setSamplesOpen(false);
    setDialogOpen(true);
  };

  const openEdit = (inquiry: ReservationInquiry) => {
    setEditing(inquiry);
    setForm({
      type: inquiry.type,
      content: inquiry.content,
      required: inquiry.required,
      choices: [...inquiry.choices]
        .sort((a, b) => a.sortIndex - b.sortIndex)
        .map((c) => c.label),
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const updateChoice = (index: number, value: string) => {
    setForm({ ...form, choices: form.choices.map((c, i) => (i === index ? value : c)) });
  };

  const submit = () => {
    if (!form.content.trim()) {
      setFormError('質問文を入力してください。');
      return;
    }
    const choices = form.choices.map((c) => c.trim()).filter((c) => c !== '');
    if (form.type === 'CHOICE' && choices.length === 0) {
      setFormError('選択式の場合は選択肢を1件以上入力してください。');
      return;
    }
    // sortIndex は新規なら末尾（既存最大+1）、編集なら現状維持
    const sortIndex = editing
      ? editing.sortIndex
      : inquiries.length > 0
        ? Math.max(...inquiries.map((i) => i.sortIndex)) + 1
        : 0;
    const input = {
      type: form.type,
      content: form.content.trim(),
      required: form.required,
      sortIndex,
      choices: form.type === 'CHOICE' ? choices : [],
    };
    setBusy(true);
    const action = editing
      ? dispatch(updateInquiry({ storeId, inquiryId: editing.id, input }))
      : dispatch(createInquiry({ storeId, input }));
    action
      .unwrap()
      .then(() => {
        setDialogOpen(false);
        showSaved();
      })
      .catch(showError)
      .finally(() => setBusy(false));
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setBusy(true);
    dispatch(deleteInquiry({ storeId, inquiryId: deleteTarget.id }))
      .unwrap()
      .then(() => showSaved())
      .catch(showError)
      .finally(() => {
        setDeleteTarget(null);
        setBusy(false);
      });
  };

  if (!loaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <TabDescription text="アレルギーの有無や利用目的など、予約時にお客様へ確認したい質問を設定できます。回答は予約詳細で確認できます" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
        <Button variant="outlined" size="small" onClick={() => setSamplesOpen(true)}>
          サンプルから追加
        </Button>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}>
          質問を追加
        </Button>
      </Box>

      {inquiries.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          質問はまだ登録されていません。
        </Typography>
      ) : (
        <Paper elevation={2}>
          <List dense>
            {inquiries.map((inquiry) => (
              <ListItem
                key={inquiry.id}
                secondaryAction={
                  <>
                    <IconButton size="small" onClick={() => openEdit(inquiry)} aria-label="編集">
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTarget(inquiry)}
                      aria-label="削除"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2">{inquiry.content}</Typography>
                      <Chip size="small" label={TYPE_LABELS[inquiry.type]} />
                      {inquiry.required && <Chip size="small" color="warning" label="必須" />}
                    </Box>
                  }
                  secondary={
                    inquiry.type === 'CHOICE' && inquiry.choices.length > 0
                      ? [...inquiry.choices]
                          .sort((a, b) => a.sortIndex - b.sortIndex)
                          .map((c) => c.label)
                          .join(' / ')
                      : null
                  }
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* 追加/編集ダイアログ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editing ? '質問を編集' : '質問を追加'}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {formError}
            </Alert>
          )}
          <TextField
            select
            label="形式"
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value as InquiryType })}
            fullWidth
            margin="dense"
            size="small"
          >
            <MenuItem value="TEXT">自由記述</MenuItem>
            <MenuItem value="CHOICE">選択式</MenuItem>
          </TextField>
          <TextField
            label="質問文"
            value={form.content}
            onChange={(e) => setForm({ ...form, content: e.target.value })}
            fullWidth
            margin="dense"
            size="small"
            multiline
            minRows={2}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.required}
                onChange={(e) => setForm({ ...form, required: e.target.checked })}
              />
            }
            label="回答を必須にする"
          />
          {form.type === 'CHOICE' && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                選択肢
              </Typography>
              {form.choices.map((choice, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <TextField
                    value={choice}
                    onChange={(e) => updateChoice(index, e.target.value)}
                    size="small"
                    fullWidth
                    placeholder={`選択肢 ${index + 1}`}
                  />
                  <IconButton
                    size="small"
                    onClick={() =>
                      setForm({ ...form, choices: form.choices.filter((_, i) => i !== index) })
                    }
                    aria-label="選択肢を削除"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setForm({ ...form, choices: [...form.choices, ''] })}
              >
                選択肢を追加
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setDialogOpen(false)} disabled={busy}>
            キャンセル
          </Button>
          <Button variant="contained" onClick={submit} disabled={busy}>
            {busy ? <CircularProgress size={22} /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* サンプルから追加（選ぶと追加フォームにプリフィル） */}
      <Dialog open={samplesOpen} onClose={() => setSamplesOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>サンプルから追加</DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary">
            よく使われる質問の例です。選ぶと内容が入力欄に入るので、調整してから保存できます。
          </Typography>
          <List>
            {INQUIRY_SAMPLES.map((sample) => (
              <ListItem key={sample.label} disablePadding>
                <ListItemButton onClick={() => openSample(sample.form)}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2">{sample.label}</Typography>
                        <Chip size="small" label={TYPE_LABELS[sample.form.type]} />
                      </Box>
                    }
                    secondary={sample.form.content}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setSamplesOpen(false)}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認 */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="質問の削除"
        message={`「${deleteTarget?.content ?? ''}」を削除します。よろしいですか？`}
        confirmText="削除する"
        confirmColor="error"
        closeColor="inherit"
      />

      <SavedSnackbar open={savedOpen} onClose={closeSaved} />
    </Box>
  );
};

export default InquiriesTab;
